import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, Subject } from 'rxjs';

import { WebSocketService } from '@core/websocket/websocket.service';
import { WsProtobufService } from '@core/websocket/ws-protobuf.service';
import { SessionService } from '@core/services/session.service';
import {
  ActiveDriverRequest,
  ActiveDriverRest,
  DriverReceiveWorkingShiftEvent,
  DriverRestOptions,
  DriverRestType,
  DriverStateEvent,
  SessionType,
} from '@core/websocket/websocket.models';
import { EventId, RequestPath, ResponseStatus } from '@core/websocket/websocket.constants';

export class RestLimitError extends Error {
  constructor() { super('rest_limit'); }
}

/** Сервер відмовив у запиті на відпочинок (наприклад, code=rest_not_allowed_time) */
export class RestNotAllowedError extends Error {
  constructor() { super('rest_not_allowed'); }
}

export type RestStep =
  | 'loading'        // Завантаження типів відпочинку
  | 'select'         // Вибір типу відпочинку
  | 'pending'        // Запит надіслано, чекаємо підтвердження
  | 'rejected'       // Запит скасовано/відхилено сервером
  | 'approved'       // Запит підтверджено, можна почати відпочинок
  | 'cancel-pending' // Запит на скасування надіслано, чекаємо підтвердження оператора
  | 'active'         // Відпочинок в процесі
  | 'expired';       // Час відпочинку вичерпано

@Injectable({ providedIn: 'root' })
export class RestService implements OnDestroy {
  private readonly ws = inject(WebSocketService);
  private readonly proto = inject(WsProtobufService);
  private readonly sessionService = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── State signals ────────────────────────────────────────────────────────

  private readonly _step = signal<RestStep>('loading');
  readonly step = this._step.asReadonly();

  private readonly _restTypes = signal<DriverRestType[]>([]);
  readonly restTypes = this._restTypes.asReadonly();

  private readonly _selectedType = signal<DriverRestType | null>(null);
  readonly selectedType = this._selectedType.asReadonly();

  /** Адреса місця відпочинку (потрібна коли requireAddress = true) */
  private readonly _addressName = signal('');
  readonly addressName = this._addressName.asReadonly();

  /** Чи показувати поле адреси */
  readonly requiresAddress = computed(() => this._selectedType()?.requireAddress ?? false);

  /** Кнопка "Відправити" активна коли тип вибрано і, якщо потрібно, введена адреса */
  readonly canSend = computed(() => {
    const type = this._selectedType();
    if (!type) return false;
    if (type.requireAddress) return this._addressName().trim().length > 0;
    return true;
  });

  /** Назва типу відпочинку для відображення на екранах статусу */
  private readonly _restTitle = signal('');
  readonly restTitle = this._restTitle.asReadonly();

  /**
   * Стріляє лише при відхиленні через реальний WS-івент (DriverRequestRejected).
   * Не стріляє при ініціалізації з lastDriverState/workingShiftData.
   */
  private readonly _rejectedViaWsEvent$ = new Subject<void>();
  readonly rejectedViaWsEvent$ = this._rejectedViaWsEvent$.asObservable();

  /** Емітує коли оператор підтвердив запит на скасування відпочинку */
  readonly cancelApprovedConfirmed$ = new Subject<void>();

  /** Емітує коли оператор відхилив запит на скасування відпочинку */
  private readonly _cancelRejectedViaWsEvent$ = new Subject<void>();
  readonly cancelRejectedViaWsEvent$ = this._cancelRejectedViaWsEvent$.asObservable();

  /** ID активного запиту (pending/approved) */
  private readonly _activeRequestId = signal<string | null>(null);

  /** ID запиту на скасування (type=CancelDriverRequest), що очікує підтвердження */
  private readonly _cancelRequestId = signal<string | null>(null);

  /**
   * Івенти підтвердження/відхилення скасування, що прилетіли ДО відповіді на
   * RequestCancelDriverRequest (event/response ordering race). Поки запит в
   * польоті, _step ще 'approved' і _cancelRequestId ще null — звичайні умови в
   * підписках не спрацюють і підтвердження було б втрачено. Такі івенти
   * буферизуються і відтворюються після декодування cancelId з відповіді.
   */
  private _bufferedCancelConfirmations: string[] = [];
  private _bufferedCancelRejections: string[] = [];
  private _cancelRequestInFlight = false;

  /**
   * Стан відпочинку з DriverStateEvent, що прилетів ДО відповіді на
   * StartDriverRest (event/response ordering race). Поки запит в польоті,
   * _step ще 'approved', тому звичайна ресинхронізація в підписці не спрацює
   * і стан було б втрачено. Буферизується і застосовується в startRest() після
   * декодування відповіді — щоб таймер стартував з серверного startedAt
   * (включно з еляпс-режимом PersonalRest) без повторного DriverStateEvent.
   */
  private _bufferedStartRest: ActiveDriverRest | null = null;
  private _startRestInFlight = false;

  /** ID активного відпочинку (active/expired) */
  private readonly _activeRestId = signal<string | null>(null);

  /**
   * ID запитів/відпочинків, скасованих у цій сесії.
   * Потрібно щоб ігнорувати їх у init(), коли lastDriverState ще застарілий
   * (сервер не встиг надіслати оновлений DriverStateEvent після скасування).
   */
  private readonly _cancelledIds = new Set<string>();

  /** Секунди для таймера (зворотний відлік або еляпс для PersonalRest) */
  private readonly _remainingSeconds = signal(0);
  readonly remainingSeconds = this._remainingSeconds.asReadonly();

  /** Чи поточний відпочинок — PersonalRest (таймер іде вгору, не завершується за duration) */
  private readonly _isPersonalRest = signal(false);
  readonly isPersonalRest = this._isPersonalRest.asReadonly();

  /** Повна тривалість поточного відпочинку (секунди). Визначає формат відображення. */
  private readonly _timerDuration = signal(0);

  readonly formattedTime = computed(() => {
    const s = this._remainingSeconds();
    // PersonalRest — таймер показує еляпс, формат визначаємо за поточним значенням
    const useHours = this._isPersonalRest() ? s >= 3600 : this._timerDuration() >= 3600;
    if (useHours) {
      const hh = Math.floor(s / 3600).toString().padStart(2, '0');
      const mm = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
      const ss = (s % 60).toString().padStart(2, '0');
      return `${hh}:${mm}:${ss}`;
    }
    const mm = Math.floor(s / 60).toString().padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  });

  private timerInterval: ReturnType<typeof setInterval> | null = null;

  // ─── Init ─────────────────────────────────────────────────────────────────

  constructor() {
    // DriverRequestConfirmed → 'approved' або підтвердження скасування
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestConfirmed),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestConfirmedEvent(e.payload);
        if (this._step() === 'pending' && event.id === this._activeRequestId()) {
          this._step.set('approved');
        } else if (this._step() === 'cancel-pending' && event.id === this._cancelRequestId()) {
          this.resetState();
          this.cancelApprovedConfirmed$.next();
        } else if (this._step() === 'approved' && this._cancelRequestInFlight) {
          // Івент підтвердження скасування прийшов раніше за відповідь — буферизуємо.
          this._bufferedCancelConfirmations.push(event.id);
        }
      });

    // DriverRequestRejected → 'rejected' або відхилення cancel
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestRejected),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestEvent(e.payload);
        if (this._step() === 'pending' && event.id === this._activeRequestId()) {
          this._step.set('rejected');
          this._rejectedViaWsEvent$.next();
        } else if (this._step() === 'cancel-pending' && event.id === this._cancelRequestId()) {
          this._cancelRequestId.set(null);
          this._step.set('approved');
          this._cancelRejectedViaWsEvent$.next();
        } else if (this._step() === 'approved' && this._cancelRequestInFlight) {
          // Івент відхилення скасування прийшов раніше за відповідь — буферизуємо.
          this._bufferedCancelRejections.push(event.id);
        }
      });

    // DriverStateEvent → синхронізація активного відпочинку
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverStateEvent),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        this.handleDriverStateEvent(this.proto.decodeDriverStateEvent(e.payload));
      });

    // DriverRestFinished → повернення до 'select'
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRestFinished),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.stopTimer();
        this._step.set('select');
      });
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /** Ініціалізація сервісу при відкритті сторінки відпочинку */
  async init(): Promise<void> {
    // Якщо вже ініціалізовано — лише підвантажуємо типи якщо потрібно.
    // Таймер продовжує йти, стан не скидається — тому немає флешу при поверненні.
    if (this._step() !== 'loading') {
      try { await this.loadRestTypes(); } catch { /* ignore */ }
      return;
    }

    try {
      await this.loadRestTypes();
    } catch (err) {
      console.error('[Rest] Failed to load rest types:', err);
    }

    // lastDriverState пріоритетний над snapshot: він актуальний і не містить
    // вже скасованих/завершених запитів (на відміну від workingShiftData).
    const liveState = this.sessionService.lastDriverState();
    if (liveState) {
      this.initFromDriverStateEvent(liveState);
      return;
    }

    const wshData = this.sessionService.workingShiftData();
    if (wshData) {
      this.initFromWorkingShiftData(wshData);
    } else {
      this._step.set('select');
    }
  }

  selectType(type: DriverRestType): void {
    this._selectedType.set(type);
    this._addressName.set('');
  }

  setAddress(address: string): void {
    this._addressName.set(address);
  }

  /** Надіслати запит на відпочинок */
  async sendRequest(): Promise<void> {
    const type = this._selectedType();
    const wshData = this.sessionService.workingShiftData();
    if (!type || !wshData) return;

    // Для типів без обов'язкової адреси передаємо displayName як значення
    const addressName = type.requireAddress
      ? this._addressName().trim()
      : type.displayName;

    const sessionId = '';
    const payloadBytes = this.proto.encodeCreateDriverRestPayload(type.id, addressName);
    const body = this.proto.encodeCreateDriverRequest(
      wshData.workingShift.id,
      SessionType.StartDriverRest,
      sessionId,
      payloadBytes,
    );

    const response = await this.ws.sendRequest(RequestPath.CreateDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      const isRestLimit = response.code === 'validation' &&
        response.properties?.some((p) => {
          if (p.name !== 'validation_violation') return false;
          const bytes = p.value instanceof Uint8Array ? p.value : null;
          if (!bytes) return false;
          const any = this.proto.tryDecodeAny(bytes);
          if (!any) return false;
          const inner = this.proto.tryDecodeGenericProto(any.value);
          return inner?.['field_1'] === 'rest_limit';
        });
      if (isRestLimit) throw new RestLimitError();
      if (response.code === 'rest_not_allowed_time') throw new RestNotAllowedError();
      throw new Error(`CreateDriverRequest failed: ${response.code}`);
    }

    if (response.body?.length) {
      const parsed = this.proto.decodeCreateDriverRequestResponse(response.body);
      this._activeRequestId.set(parsed.id);
    }

    this._restTitle.set(type.displayName);
    this._isPersonalRest.set(type.restOption === DriverRestOptions.PersonalRest);
    this._timerDuration.set(type.duration);
    // PersonalRest: таймер рахує вгору від startedAt, тому на етапі pending показуємо 0
    this._remainingSeconds.set(this._isPersonalRest() ? 0 : type.duration);
    this._step.set('pending');
  }

  /** Почати відпочинок після підтвердження запиту */
  async startRest(): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    // Поки запит в польоті, DriverStateEvent з driverRest.startedAt
    // буферизується (step ще 'approved' — звичайна ресинхронізація не спрацює).
    this._startRestInFlight = true;
    try {
      const body = this.proto.encodeStartDriverRest(requestId);
      const response = await this.ws.sendRequest(RequestPath.StartDriverRest, body);
      if (response.status !== ResponseStatus.Success) {
        throw new Error(`StartDriverRest failed: ${response.code}`);
      }

      if (response.body?.length) {
        const parsed = this.proto.decodeStartDriverRestResponse(response.body);
        this._activeRestId.set(parsed.id);
      }

      const buffered = this._bufferedStartRest;
      this._bufferedStartRest = null;

      if (buffered?.startedAt) {
        // Відпочинок вже почався на сервері, поки запит був в польоті.
        // Стартуємо/ресинхронізуємо таймер з серверного startedAt замість
        // запасного старту — повторний DriverStateEvent не потрібен.
        // ID з відповіді пріоритетний над буферизованим.
        if (!this._activeRestId()) this._activeRestId.set(buffered.id);
        const isPersonal = buffered.restOption === DriverRestOptions.PersonalRest;
        const elapsed = Math.floor(Date.now() / 1000) - Number(buffered.startedAt.seconds);
        const timerSeconds = isPersonal
          ? Math.max(0, elapsed)
          : Math.max(0, buffered.duration - elapsed);
        this._isPersonalRest.set(isPersonal);
        this._timerDuration.set(buffered.duration);
        this._step.set('active');
        this.stopTimer();
        this._remainingSeconds.set(timerSeconds);
        this.startTimer(timerSeconds, isPersonal);
        return;
      }

      // Таймер буде запущений через DriverStateEvent з ActiveDriverRest.startedAt
      // Поки ставимо 'active' і чекаємо на DriverStateEvent для синхронізації таймера
      this._step.set('active');
      // Запасний старт — якщо DriverStateEvent не прийде.
      // PersonalRest рахує вгору від 0, решта — вниз від поточного значення.
      if (this._isPersonalRest()) {
        this.startTimer(0, true);
      } else {
        this.startTimer(this._remainingSeconds());
      }
    } finally {
      this._startRestInFlight = false;
      this._bufferedStartRest = null;
    }
  }

  /** Завершити відпочинок */
  async finishRest(): Promise<void> {
    const restId = this._activeRestId();
    if (!restId) return;

    const body = this.proto.encodeFinishDriverRest(restId);
    await this.ws.sendRequest(RequestPath.FinishDriverRest, body);
    this.stopTimer();
    this.resetState();
  }

  /** Скасувати запит (pending або approved стан) */
  async cancelRequest(): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    this._cancelledIds.add(requestId);
    const body = this.proto.encodeCancelDriverRequest(requestId);
    const response = await this.ws.sendRequest(RequestPath.CancelDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      this._cancelledIds.delete(requestId);
      throw new Error(`CancelDriverRequest failed: ${response.code}`);
    }
    this.resetState();
  }

  /** Надіслати запит на скасування підтвердженого запиту (approved стан) */
  async requestCancelApprovedRequest(): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    // Поки запит в польоті, івенти підтвердження/відхилення буферизуються,
    // бо _step ще 'approved' і _cancelRequestId ще null.
    this._cancelRequestInFlight = true;
    try {
      const body = this.proto.encodeCancelDriverRequest(requestId);
      const response = await this.ws.sendRequest(RequestPath.RequestCancelDriverRequest, body);
      if (response.status !== ResponseStatus.Success) {
        throw new Error(`RequestCancelApprovedRequest failed: ${response.code}`);
      }

      if (response.body?.length) {
        const cancelId = this.proto.decodeCancelRequestResponse(response.body);
        if (cancelId) {
          this._cancelRequestId.set(cancelId);

          // Відтворення буферизованих івентів, що прилетіли до відповіді.
          if (this._bufferedCancelConfirmations.includes(cancelId)) {
            this.resetState();
            this.cancelApprovedConfirmed$.next();
            return;
          }
          if (this._bufferedCancelRejections.includes(cancelId)) {
            this._bufferedCancelRejections = [];
            this._cancelRequestId.set(null);
            this._step.set('approved');
            this._cancelRejectedViaWsEvent$.next();
            return;
          }
        }
      }

      this._step.set('cancel-pending');
    } finally {
      this._cancelRequestInFlight = false;
      this._bufferedCancelConfirmations = [];
      this._bufferedCancelRejections = [];
    }
  }

  resetState(): void {
    this.stopTimer();
    this._step.set('select');
    this._selectedType.set(null);
    this._addressName.set('');
    this._activeRequestId.set(null);
    this._cancelRequestId.set(null);
    this._activeRestId.set(null);
    this._restTitle.set('');
    this._isPersonalRest.set(false);
    this._timerDuration.set(0);
    this._remainingSeconds.set(0);
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private async loadRestTypes(): Promise<void> {
    if (this._restTypes().length > 0) return;

    const response = await this.ws.sendRequest(RequestPath.GetRestTypes);
    if (response.status === ResponseStatus.Success && response.body?.length) {
      this._restTypes.set(this.proto.decodeDriverRestTypes(response.body));
    }
  }

  /**
   * Ініціалізація стану з актуального DriverStateEvent.
   * На відміну від snapshot, тут вже немає скасованих/завершених запитів.
   */
  private initFromDriverStateEvent(event: DriverStateEvent): void {
    if (event.driverRest && !this._cancelledIds.has(event.driverRest.id)) {
      this.applyActiveRest(event.driverRest);
      return;
    }

    const restRequest = event.requests.find(
      (r) => r.type === SessionType.StartDriverRest && !this._cancelledIds.has(r.id),
    );
    if (restRequest) {
      this.applyRestRequestMeta(restRequest);
      if (restRequest.status === 'confirmed') {
        this._step.set('approved');
      } else {
        this._step.set('pending');
      }
      return;
    }

    this._step.set('select');
  }

  private initFromWorkingShiftData(data: DriverReceiveWorkingShiftEvent): void {
    // Активний відпочинок має пріоритет
    if (data.driverRest && !this._cancelledIds.has(data.driverRest.id)) {
      this.applyActiveRest(data.driverRest);
      return;
    }

    // Шукаємо активний запит типу StartDriverRest (не скасований)
    const restRequest = data.activeDriverRequests.find(
      (r) => r.type === SessionType.StartDriverRest && !this._cancelledIds.has(r.id),
    );

    if (restRequest) {
      this.applyRestRequestMeta(restRequest);

      if (restRequest.status === 'confirmed') {
        this._step.set('approved');
      } else {
        this._step.set('pending');
      }
      return;
    }

    this._step.set('select');
  }

  /**
   * Відновлює тип/назву/тривалість/PersonalRest-прапор з payload запиту.
   * Спільний шлях для init з DriverStateEvent та workingShiftData snapshot,
   * щоб обидва джерела відновлювали однаковий набір метаданих.
   */
  private applyRestRequestMeta(restRequest: ActiveDriverRequest): void {
    this._activeRequestId.set(restRequest.id);

    if (restRequest.payload?.length) {
      try {
        const payload = this.proto.decodeCreateDriverRestPayload(restRequest.payload as Uint8Array);
        const type = this._restTypes().find((t) => t.id === payload.typeId);
        if (type) {
          this._selectedType.set(type);
          this._restTitle.set(type.displayName);
          this._isPersonalRest.set(type.restOption === DriverRestOptions.PersonalRest);
          this._timerDuration.set(type.duration);
        }
      } catch {
        // payload may be in expanded (non-bytes) form — ignore
      }
    }

    // PersonalRest: таймер рахує вгору, на етапі pending/approved показуємо 0
    this._remainingSeconds.set(this._isPersonalRest() ? 0 : this._timerDuration());
  }

  private applyActiveRest(rest: ActiveDriverRest): void {
    this._activeRestId.set(rest.id);
    this._restTitle.set(rest.displayName);
    this._timerDuration.set(rest.duration);
    // PersonalRest-прапор відновлюємо з серверних даних, а не зі стану сигналів
    this._isPersonalRest.set(rest.restOption === DriverRestOptions.PersonalRest);
    const type = this._restTypes().find((t) => t.id === rest.typeId);
    if (type) {
      this._selectedType.set(type);
    }

    // PersonalRest: таймер рахує ВГОРУ від startedAt і не переходить в 'expired'
    if (rest.restOption === DriverRestOptions.PersonalRest) {
      this._isPersonalRest.set(true);
      if (rest.startedAt) {
        const elapsed = Math.max(0, Math.floor(Date.now() / 1000) - Number(rest.startedAt.seconds));
        this._remainingSeconds.set(elapsed);
        this.startTimer(elapsed, true);
        this._step.set('active');
      } else {
        // Підтверджено, але ще не почато
        this._remainingSeconds.set(0);
        this._step.set('approved');
      }
      return;
    }

    if (rest.startedAt) {
      const elapsed = Math.floor(Date.now() / 1000) - Number(rest.startedAt.seconds);
      const remaining = Math.max(0, rest.duration - elapsed);
      this._remainingSeconds.set(remaining);

      if (remaining > 0) {
        this.startTimer(remaining);
        this._step.set('active');
      } else {
        this._step.set('expired');
      }
    } else {
      // Підтверджено, але ще не почато
      this._remainingSeconds.set(rest.duration);
      this._step.set('approved');
    }
  }

  private handleDriverStateEvent(event: DriverStateEvent): void {
    // Ініціалізація після page refresh — DriverStateEvent прийшов до/замість init()
    if (this._step() === 'loading') {
      this.initFromDriverStateEvent(event);
      return;
    }

    if (event.driverRest) {
      const rest = event.driverRest;

      if (rest.startedAt && this._step() === 'active') {
        // Ресинхронізація таймера з сервером (тільки коли вже в стані active).
        // PersonalRest-режим беремо з rest.restOption, а не з можливого
        // застарілого сигналу _isPersonalRest.
        const isPersonal = rest.restOption === DriverRestOptions.PersonalRest;
        const elapsed = Math.floor(Date.now() / 1000) - Number(rest.startedAt.seconds);
        const timerSeconds = isPersonal
          ? Math.max(0, elapsed)
          : Math.max(0, rest.duration - elapsed);
        this._activeRestId.set(rest.id);
        this._isPersonalRest.set(isPersonal);
        this.stopTimer();
        this._remainingSeconds.set(timerSeconds);
        this.startTimer(timerSeconds, isPersonal);
      } else if (!rest.startedAt && this._step() === 'approved') {
        // Оновлюємо тривалість на frozen timer (approved — відпочинок ще не розпочато)
        this._timerDuration.set(rest.duration);
        this._remainingSeconds.set(rest.duration);
        this._restTitle.set(rest.displayName);
      } else if (rest.startedAt && this._step() === 'approved' && this._startRestInFlight) {
        // Race: відпочинок почався на сервері, поки StartDriverRest ще в польоті
        // (step ще 'approved'). Не discarding — буферизуємо серверний стан;
        // startRest() застосує його після відповіді і стартує таймер від startedAt.
        this._bufferedStartRest = rest;
      }
      // Якщо startedAt присутній, але стан approved — ігноруємо:
      // перехід approved→active відбувається виключно через startRest().
    }

    // Оновлюємо requestId якщо потрібно
    if (this._step() === 'pending' || this._step() === 'approved') {
      const restRequest = event.requests.find((r) => r.type === SessionType.StartDriverRest);
      if (restRequest) {
        this._activeRequestId.set(restRequest.id);
      }
    }
  }

  private startTimer(initialSeconds: number, countUp = false): void {
    this.stopTimer();
    this._remainingSeconds.set(initialSeconds);

    this.timerInterval = setInterval(() => {
      const current = this._remainingSeconds();
      if (countUp) {
        // PersonalRest — еляпс, не завершується за duration
        this._remainingSeconds.update((s) => s + 1);
        return;
      }
      if (current <= 0) {
        this.stopTimer();
        this._step.set('expired');
        return;
      }
      this._remainingSeconds.update((s) => s - 1);
    }, 1_000);
  }

  private stopTimer(): void {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
}
